import { useState } from "react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AnimatedLoginButton } from "@/components/animated-login-button"
import { AuthBackToSignInLink } from "@/components/auth/auth-back-to-sign-in-link"
import { API_ORIGIN, CURRENT_CLIENT } from "@/constants"

interface PasswordResetRequestProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
}

export function PasswordResetRequest({ className, onSuccess, ...props }: PasswordResetRequestProps) {
  const [email, setEmail] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccess(false)

    if (!email) {
      setError("Please enter your email address")
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch(`${API_ORIGIN}/_allauth/app/v1/auth/password/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(true)
        onSuccess?.()
      } else {
        setError(data.error || data.detail || 'Failed to send password reset email')
      }
    } catch (error) {
      console.error('Password reset error:', error)
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          {error && (
            <div className="p-4 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10">
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
              Password reset email sent! Please check your email for instructions.
            </div>
          )}
          {!success && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={CURRENT_CLIENT.placeholderEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <AnimatedLoginButton
                disabled={isLoading}
                isLoading={isLoading}
                mode="passwordResetRequest"
              />
            </>
          )}
          <AuthBackToSignInLink label="back" />
        </div>
      </form>
    </div>
  )
}
